import { getUrlPrefix } from '~/lang/locales'
import {
  availabilityCalendarResponseSchema,
  type AvailabilityCalendarResponse,
} from './schemas'
import { AVAILABILITY_CALENDAR_ENDPOINT } from './config'
import { CalendarFetchError, CalendarValidationError } from './CalendarError'

// Default timeout for availability requests (30 seconds - lighter endpoint)
const AVAILABILITY_TIMEOUT = 30000

export class AvailabilityCalendarRepositoryFetch {
  /**
   * Fetch availability calendar data from Calendar API v2
   * This endpoint provides base pricing for ALL bookable dates with minimum stay requirements
   */
  async get(
    langCode: string,
    params: Record<string, any>, // Already mapped API parameters
    signal?: AbortSignal,
  ): Promise<AvailabilityCalendarResponse> {
    // Build query parameters
    const queryParams = new URLSearchParams()

    // Build query string from API parameters
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.set(key, String(value))
      }
    })

    const urlPrefix = getUrlPrefix(langCode)
    const url = `/_/fallback${urlPrefix}${AVAILABILITY_CALENDAR_ENDPOINT}?${queryParams.toString()}`

    // Separate error handling for fetch vs validation
    let response: any

    // Step 1: Fetch the data
    const requestStartTime = Date.now()
    try {
      response = await $fetch<AvailabilityCalendarResponse>(url, {
        signal,
        timeout: AVAILABILITY_TIMEOUT,
      })
    } catch (error: any) {
      // Silently return empty response for abort errors - they're intentional cancellations
      if (error?.name === 'AbortError' || error?.message?.includes('aborted')) {
        return { calendar_data: {} } as any as AvailabilityCalendarResponse
      }

      // Handle 401/403 as bot protection - return empty calendar gracefully
      // These responses are typically Cloudflare blocking bots/crawlers
      // 401: Cloudflare blocking unauthenticated bots
      // 403: Cloudflare blocking suspected malicious traffic
      // Returning empty calendar prevents error state and Sentry noise
      const httpStatus =
        error?.statusCode || error?.status || error?.response?.status
      if (httpStatus === 401 || httpStatus === 403) {
        if (process.dev) {
          console.warn(
            `[AvailabilityCalendar] ${httpStatus} ${httpStatus === 401 ? 'Unauthorized' : 'Forbidden'} - likely bot protection`,
            {
              url,
              params,
            },
          )
        }
        return { calendar_data: {} } as any as AvailabilityCalendarResponse
      }

      // Enhance error with timing information for better debugging
      // Note: BaseApiFetchError handles status code extraction from FetchError
      const requestDuration = Date.now() - requestStartTime
      const enhancedError = {
        ...error,
        requestTiming: {
          startTime: requestStartTime,
          duration: requestDuration,
          timeoutConfigured: AVAILABILITY_TIMEOUT,
          wasNearTimeout: requestDuration > AVAILABILITY_TIMEOUT * 0.9,
        },
      }

      const calendarError = new CalendarFetchError(
        'availability',
        url,
        params,
        enhancedError,
      )
      // Don't log here - let the store handle logging to avoid duplicates
      throw calendarError
    }

    // Step 2: Validate with Zod schema
    try {
      return availabilityCalendarResponseSchema.parse(response)
    } catch (error: any) {
      const validationError = new CalendarValidationError(
        'availability',
        url,
        params,
        response,
        error,
      )
      // Don't log here - let the store handle logging to avoid duplicates
      throw validationError
    }
  }
}
